import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { MessageCircle, PanelRight, PanelsTopLeft, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import ThreadList, { emptyThreadListState } from "./ThreadList";

const MessagesContext = createContext<{
  openMessages: (threadId?: string) => void;
  open: boolean;
} | null>(null);

export const usePortalMessages = () => useContext(MessagesContext);

export function PortalMessagesButton() {
  const messages = usePortalMessages();
  const { t } = useTranslation("portal");
  if (!messages) return null;
  return (
    <Button variant="ghost" size="icon" onClick={() => messages.openMessages()}
      aria-label={t("dashboard.messages")} title={t("dashboard.messages")}
      aria-haspopup="dialog" aria-expanded={messages.open}>
      <MessageCircle className="h-5 w-5" />
    </Button>
  );
}

export function PortalMessagesProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation("portal");
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(emptyThreadListState);
  const [floating, setFloating] = useState(false);
  const [threadId, setThreadId] = useState<string>();
  const [selection, setSelection] = useState(0);
  const opener = useRef<HTMLElement | null>(null);
  const openMessages = (id?: string) => {
    opener.current = document.activeElement as HTMLElement;
    if (id) { setThreadId(id); setSelection((value) => value + 1); }
    setOpen(true);
  };
  return (
    <MessagesContext.Provider value={{ openMessages, open }}>
      {children}
      {!open && (
        <Button onClick={() => openMessages()} className="fixed bottom-5 right-5 z-40 gap-2 rounded-full shadow-lg"
          aria-haspopup="dialog" aria-expanded={false}>
          <MessageCircle className="h-5 w-5" />{t("dashboard.messages")}
        </Button>
      )}
      <Dialog.Root open={open} onOpenChange={setOpen} modal={mobile}>
        <Dialog.Portal>
          <Dialog.Content
            onInteractOutside={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => { event.preventDefault(); opener.current?.focus(); }}
            className={`fixed z-[60] flex flex-col border bg-background shadow-2xl outline-none ${
              floating && !mobile
                ? "bottom-5 right-5 h-[min(720px,calc(100dvh-100px))] w-[440px] max-w-[calc(100vw-40px)] rounded-2xl"
                : "inset-y-0 right-0 h-dvh w-full sm:max-w-lg"
            }`}>
            <div className="flex items-center gap-2 border-b p-4">
              <Dialog.Title className="flex-1 font-heading text-lg font-bold">{t("dashboard.messages")}</Dialog.Title>
              {!mobile && (
                <Button variant="ghost" size="icon" onClick={() => setFloating(!floating)}
                  aria-label={floating ? "Dock messages to sidebar" : "Float messages"}>
                  {floating ? <PanelRight className="h-4 w-4" /> : <PanelsTopLeft className="h-4 w-4" />}
                </Button>
              )}
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" aria-label={t("messages.closePanel", { defaultValue: "Close messages" })}><X className="h-4 w-4" /></Button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="sr-only">Send a message to support or continue a past discussion.</Dialog.Description>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <ThreadList key={selection} initialThreadId={threadId} hideHeading state={conversation} onStateChange={setConversation} onThreadOpened={() => setThreadId(undefined)} />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </MessagesContext.Provider>
  );
}
